import React, { useEffect, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { cn } from '@client/lib';

interface CustomAmountInputProps {
  name: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  updateClient?: (value: number) => void;
  formatValue?: (value: number) => string | number;
  testId?: string;
}

function CustomAmountInput({
  name,
  value,
  min,
  max,
  onChange,
  updateClient,
  formatValue,
  testId,
}: CustomAmountInputProps) {
  const [localValue, setLocalValue] = useState<string>(String(value || ''));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused && typeof value === 'number' && !isNaN(value)) {
      setLocalValue(String(value));
    }
  }, [value, isFocused]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = e.target.value;
    setLocalValue(inputValue);
    if (inputValue !== '' && !isNaN(parseFloat(inputValue))) {
      const numValue = parseFloat(inputValue);
      if (numValue >= min / 1000) {
        const clampedValue = Math.max(min, Math.min(max, numValue));
        onChange(clampedValue);
      }
    }
  }

  function handleBlur() {
    setIsFocused(false);
    const parsedValue = localValue === '' ? min : parseFloat(localValue) || min;
    const clampedValue = Math.max(min, Math.min(max, parsedValue));
    setLocalValue(String(clampedValue));
    onChange(clampedValue);
    if (updateClient) {
      updateClient(clampedValue);
    }
  }

  function handleFocus() {
    setIsFocused(true);
  }

  return (
    <div className="flex items-center gap-3">
      <label htmlFor={`${name}-custom-input`} className="text-sm font-medium text-gray-700">
        Custom Amount:
      </label>
      <Input
        id={`${name}-custom-input`}
        type="number"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        className="w-32 h-9"
        placeholder={formatValue ? String(formatValue(min)) : String(min)}
        data-testid={`${testId}-custom-input`}
      />
    </div>
  );
}

function SliderTooltip({
  isHovered,
  value,
  position,
  isDefault = false,
  suffix = '',
}: {
  isHovered: boolean;
  value: number | string;
  position: number; // 0-100 percentage
  isDefault?: boolean;
  suffix?: string;
}) {
  // Adjust position to ensure tooltip doesn't go outside container on either side
  // Minimum 8% from left, maximum 92% from left (8% from right)
  const adjustedPosition = Math.min(Math.max(position, 8), 92);
  return (
    <div
      className={`pointer-events-none absolute z-50 -mt-10 -translate-x-1/2 transform whitespace-nowrap rounded px-2 py-1 text-xs text-white shadow-md transition-opacity ${
        isHovered ? 'opacity-100' : 'opacity-0'
      } ${isDefault ? 'bg-primary' : 'bg-primary-dark'}`}
      style={{
        left: `${adjustedPosition}%`,
      }}
    >
      <span className="font-semibold">
        {value}
        {suffix}
      </span>
    </div>
  );
}

interface CustomSliderProps {
  'name': string;
  'control': Control<any>;
  'min': number;
  'max': number;
  'step'?: number;
  'defaultValue': number;
  'formatValue'?: (value: number) => string | number;
  'updateClient'?: (value: number) => void;
  'labelSuffix'?: string;
  'defaultLabel'?: string;
  'hideResetButton'?: boolean;
  'label'?: string;
  'data-testid'?: string;
  'showCustomInput'?: boolean;
  'customInputLabel'?: string;
}

export function CustomSlider({
  name,
  control,
  min,
  max,
  step = 1,
  defaultValue,
  formatValue,
  updateClient,
  labelSuffix = '',
  defaultLabel = 'Default',
  hideResetButton = false,
  label,
  'data-testid': testId,
  showCustomInput = false,
  customInputLabel = 'Enter custom amount',
}: CustomSliderProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showCustomInputField, setShowCustomInputField] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const isDefault = value === defaultValue;

        function handleReset(e: React.MouseEvent) {
          e.stopPropagation();
          e.preventDefault();

          onChange(defaultValue);
          if (updateClient) {
            updateClient(defaultValue);
          }
        }

        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {label && <div className="text-primary-dark text-lg font-semibold">{label}</div>}
              <Button
                onClick={handleReset}
                variant="outline"
                type="button"
                size="sm"
                className={cn('border-primary/25 text-primary-dark ml-auto h-7 px-2 text-xs', {
                  invisible: isDefault || hideResetButton,
                })}
              >
                {defaultLabel}
              </Button>
            </div>
            <div className="relative">
              <Slider
                value={[value]}
                onValueChange={vals => {
                  onChange(vals[0]);
                  if (updateClient) {
                    updateClient(vals[0]);
                  }
                }}
                min={min}
                max={max}
                step={step}
                className="[&_[data-slot=slider-thumb]]:size-4 [&_[data-slot=slider-track]]:h-1.5"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                data-testid={testId}
              />

              <SliderTooltip
                isHovered={isHovered}
                value={formatValue ? formatValue(value) : value}
                position={((value - min) / (max - min)) * 100}
                isDefault={isDefault}
                suffix={labelSuffix}
              />
            </div>
            {showCustomInput && (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${name}-show-custom-amount`}
                    checked={showCustomInputField}
                    onCheckedChange={(checked) => setShowCustomInputField(checked === true)}
                  />
                  <label
                    htmlFor={`${name}-show-custom-amount`}
                    className="text-sm text-gray-600 cursor-pointer"
                  >
                    {customInputLabel}
                  </label>
                </div>
                {showCustomInputField && (
                  <CustomAmountInput
                    name={name}
                    value={value}
                    min={min}
                    max={max}
                    onChange={onChange}
                    updateClient={updateClient}
                    formatValue={formatValue}
                    testId={testId}
                  />
                )}
              </>
            )}
          </div>
        );
      }}
    />
  );
}
